/**
 * The booking lifecycle bar as Traci described it (review, 23 Sep 2026). Pure: facts in, twelve stage states out.
 *
 *   completed  filled (tan)                        the stage happened: a fact on the record, or a stage change in history
 *   partial    ring, half filled                   multi-talent stages where some but not all talent are done
 *   current    ring, white inside                  "where we are now": the next stage that has not happened yet
 *   skipped    grey, like not reached              passed over (Media Issue with no issue; Partial delivery when
 *                                                  everything arrived at once; a stage the booking moved past without it)
 *   unreached  grey
 *
 * Multi-talent stages (partial until every assigned talent is done): client/talent communication, 5-day check-in,
 * media uploaded, media approved & in progress, talent paid. Media Issue fills fully if any talent had an issue.
 * A later issue never un-fills Media Approved (history keeps it). Media Delivered (Full) fills Booking Complete, which
 * archives the booking.
 *
 * Bookings tab status (Traci): Pending until event day, Post after it, Completed when Booking Complete fills.
 *
 * Revert (Traci, Oct 2026; src/lib/booking/stageChange.js): after a revert mark in the history, stage marks for that stage
 * and later ones made before it no longer fill the bar, and the ring sits on the stage reverted to until it really happens.
 * Facts (a recorded payment, a delivery) still fill their stage.
 */

/** A revert in booking_stage_history: change_type 'manual', the reason starting with this tag. */
export const REVERT_TAG = '[revert]'
export const isRevertMark = h => String(h?.reason || '').startsWith(REVERT_TAG)
const markTime = h => Date.parse(h?.changed_at || h?.created_at || '') || 0
export const STAGES = [
  { key: 'booked', label: 'Booked' },
  { key: 'logistics_questionnaire_completed', label: 'Logistics Questionnaire Completed' },
  { key: 'client_talent_communication_in_progress', label: 'Client/Talent Communication in Progress', multi: true },
  { key: 'talent_5_day_check_in', label: 'Talent 5-Day Check-In', multi: true },
  { key: 'event_day', label: 'Event Day' },
  { key: 'media_uploaded', label: 'Media Uploaded', multi: true },
  { key: 'media_issue', label: 'Media Issue', optional: true },
  { key: 'media_approved', label: 'Media Approved / Media in Progress', multi: true },
  { key: 'talent_paid', label: 'Talent Paid', multi: true },
  { key: 'media_delivered_partial', label: 'Media Delivered (Partial)', optional: true },
  { key: 'media_delivered_full', label: 'Media Delivered (Full)' },
  { key: 'booking_complete', label: 'Booking Complete' }
]
export const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]))

const PLACEHOLDER_TALENT = new Set(['410935f9-6034-40b3-a65f-e9863de60e58', '7c7191dc-c5e5-43ed-b772-083463a00aa4'])
const dateOnly = v => (v ? String(v).slice(0, 10) : '')
const lower = v => String(v || '').toLowerCase()
const APPROVED = new Set(['approved', 'ready_for_delivery', 'delivered'])
const SUBMITTED = new Set(['submitted', 'completed', 'locked'])

/**
 * The questionnaire's real state (29 Sep 2026, Bailey Setzer and Vanessa Nieuwenhuis): 'submitted' | 'in_progress' |
 * 'not_started'. `status` is the logistics_questionnaire row's status, or null/undefined when there is no row.
 */
export function questionnaireState(status) {
  if (status === null || status === undefined || status === '') return 'not_started'
  return SUBMITTED.has(lower(status)) ? 'submitted' : 'in_progress'
}

/**
 * What the lifecycle bar is told about the questionnaire. Talent confirming client contact moves the stored stage past
 * "Logistics Questionnaire Completed", so the bar used to show it done for clients who never started it. For an
 * upcoming event the questionnaire row decides (no row = not done); a past event with no row keeps the stored stage
 * (older bookings predate the questionnaire).
 */
export function questionnaireFact({ status, eventDate, today }) {
  if (status !== null && status !== undefined && status !== '') return status
  const event = dateOnly(eventDate)
  return event && today && event >= today ? 'not_started' : undefined
}
const PAID_COLUMN = { photographer: 'talent_paid', second_photographer: 'second_photographer_paid', videographer: 'videographer_paid', second_videographer: 'second_videographer_paid' }

/** Real talent assignments, with the media each one produces. */
export function assignedRoles(b) {
  const out = []
  if (b?.photographer_id && !PLACEHOLDER_TALENT.has(b.photographer_id)) out.push({ role: 'photographer', id: b.photographer_id, media: 'photo' })
  if (b?.second_photographer_id && !PLACEHOLDER_TALENT.has(b.second_photographer_id)) out.push({ role: 'second_photographer', id: b.second_photographer_id, media: 'photo' })
  if (b?.videographer_id && !PLACEHOLDER_TALENT.has(b.videographer_id)) out.push({ role: 'videographer', id: b.videographer_id, media: 'video' })
  if (b?.second_videographer_id && !PLACEHOLDER_TALENT.has(b.second_videographer_id)) out.push({ role: 'second_videographer', id: b.second_videographer_id, media: 'video' })
  return out
}

/** Which media the booking delivers: photo, video or both (from the package, then from who is assigned). */
export function mediaOwed(b) {
  const t = lower(b?.package_type).replace(/[^a-z]/g, '')
  const video = t.includes('video') || assignedRoles(b).some(r => r.media === 'video')
  const photo = !(t === 'videoonly') || assignedRoles(b).some(r => r.media === 'photo')
  return { photo, video }
}

const count = (roles, fn) => ({ done: roles.filter(fn).length, total: roles.length })

/**
 * @param {object} b                booking row (lifecycle_stage, event_date, booking_status, package_type, talent ids,
 *                                  photo/video review + delivered columns, media_delivered_at, has_media_issue, paid flags)
 * @param {object} f                facts, each optional (null = unknown, fall back to the stored stage):
 *   history          booking_stage_history rows ({ to_stage })
 *   questionnaire    logistics_questionnaire status
 *   messagedRoles    Set of roles whose talent has written to the client in the booking chat
 *   checkedInRoles   Set of roles with a 5-day check-in recorded
 *   uploadedRoles    Set of roles with a submitted job completion form
 * @param {string} today            YYYY-MM-DD
 */
export function lifecycleView(b, f = {}, today) {
  const roles = assignedRoles(b)
  const owed = mediaOwed(b)
  const storedIdx = STAGE_INDEX[b?.lifecycle_stage] ?? 0
  const marks = f.history || []
  const revert = marks.filter(isRevertMark).sort((x, y) => markTime(y) - markTime(x))[0] || null
  const revertIdx = revert ? STAGE_INDEX[revert.to_stage] ?? null : null
  const history = new Set(marks.filter(h => revertIdx === null || markTime(h) > markTime(revert) || (STAGE_INDEX[h.to_stage] ?? 99) < revertIdx).map(h => h.to_stage))
  const reachedByStore = key => STAGE_INDEX[key] <= storedIdx
  const event = dateOnly(b?.event_date)
  const photoDelivered = Boolean(b?.photo_delivered_at || (!owed.video && (b?.media_delivered_at || lower(b?.review_status) === 'delivered')))
  const videoDelivered = Boolean(b?.video_delivered_at)
  const allDelivered = (!owed.photo || photoDelivered) && (!owed.video || videoDelivered)
  const complete = lower(b?.booking_status) === 'completed' || b?.lifecycle_stage === 'booking_complete'

  // Per stage: { done, total } for multi-talent stages, or true/false/null (null = no fact, use the stored stage).
  const facts = {
    booked: true,
    logistics_questionnaire_completed: f.questionnaire === undefined ? null : SUBMITTED.has(lower(f.questionnaire)),
    client_talent_communication_in_progress: f.messagedRoles && roles.length ? count(roles, r => f.messagedRoles.has(r.role)) : null,
    talent_5_day_check_in: f.checkedInRoles && roles.length ? count(roles, r => f.checkedInRoles.has(r.role)) : null,
    event_day: event ? event < today : null,
    media_uploaded: f.uploadedRoles && roles.length ? count(roles, r => f.uploadedRoles.has(r.role)) : null,
    media_issue: Boolean(b?.has_media_issue || lower(b?.photo_review_status) === 'has_issues' || lower(b?.video_review_status) === 'has_issues' || history.has('media_issue')),
    media_approved: roles.length ? count(roles, r => APPROVED.has(lower(r.media === 'video' ? b?.video_review_status : (b?.photo_review_status || b?.review_status)))) : null,
    talent_paid: roles.length ? count(roles, r => Boolean(b?.[PAID_COLUMN[r.role]])) : null,
    media_delivered_partial: owed.photo && owed.video ? (photoDelivered !== videoDelivered) || (photoDelivered && videoDelivered && history.has('media_delivered_partial')) : false,
    media_delivered_full: allDelivered && (photoDelivered || videoDelivered) ? true : null,
    booking_complete: complete || (allDelivered && (photoDelivered || videoDelivered)) ? true : null
  }

  const raw = STAGES.map(s => {
    const fact = facts[s.key]
    const inHistory = history.has(s.key)
    if (fact && typeof fact === 'object') {
      if (fact.done >= fact.total || inHistory) return { state: 'completed', done: fact.total, total: fact.total }
      if (fact.done > 0) return { state: 'partial', done: fact.done, total: fact.total }
      return { state: reachedByStore(s.key) && !s.multi ? 'completed' : 'open', done: 0, total: fact.total }
    }
    if (fact === true || inHistory) return { state: 'completed' }
    if (fact === null && !s.optional && reachedByStore(s.key)) return { state: 'completed' }
    return { state: 'open' }
  })
  // Media Delivered (Full) fills Booking Complete (and the reverse).
  if (raw[STAGE_INDEX.media_delivered_full].state === 'completed' || raw[STAGE_INDEX.booking_complete].state === 'completed') {
    raw[STAGE_INDEX.media_delivered_full] = { state: 'completed' }
    raw[STAGE_INDEX.booking_complete] = { state: 'completed' }
  }
  // Anything open before the last completed or partial stage was passed over.
  let last = -1
  raw.forEach((r, i) => { if (r.state === 'completed' || r.state === 'partial') last = i })
  let current = -1
  const stages = STAGES.map((s, i) => {
    let state = raw[i].state
    if (state === 'open') state = i < last ? 'skipped' : 'unreached'
    return { ...s, ...raw[i], state }
  })
  // The ring never sits on a stage that may legitimately never happen: Media Issue, and Partial delivery unless both
  // photo and video are owed (then photos-first is a real step).
  const optionalHere = s => s.key === 'media_issue' || (s.key === 'media_delivered_partial' && !(owed.photo && owed.video))
  const firstPartial = stages.findIndex(s => s.state === 'partial')
  current = firstPartial >= 0 ? firstPartial : stages.findIndex(s => s.state === 'unreached' && !optionalHere(s))
  // After a revert the ring sits on the stage reverted to (still the stored stage) until that stage really happens;
  // later stages not filled by a fact read as not reached.
  if (revertIdx !== null && storedIdx === revertIdx && stages[revertIdx].state !== 'completed') {
    current = revertIdx
    stages.forEach((s, i) => { if (i > revertIdx && s.state !== 'completed') s.state = 'unreached' })
    stages[revertIdx].state = 'current'
  }
  if (current >= 0 && stages[current].state === 'unreached') stages[current].state = 'current'

  const done = stages[STAGE_INDEX.booking_complete].state === 'completed'
  const cancelled = lower(b?.booking_status).startsWith('cancel')
  const post = stages[STAGE_INDEX.event_day].state === 'completed'
  const status = cancelled ? 'Cancelled' : done ? 'Completed' : post ? 'Post' : 'Pending'
  return {
    stages,
    current: current >= 0 ? stages[current].key : null,
    current_label: current >= 0 ? stages[current].label : 'Booking Complete',
    status,
    archived: done || cancelled
  }
}
